// Immutable variable revision history for the editor drawer. The caller owns
// the generated-client query so this component remains a raw View with no
// provider dependency (docs/09 §4a).

import type { components } from "../../api/schema";
import { formatDateTime, formatJsonValue } from "../../lib/format";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  Section,
  Skeleton,
  Stack,
  Text,
} from "../../ui";

type VariableRevisionResponse =
  components["schemas"]["VariableRevisionResponse"];

export type VariableHistoryResult = ListQueryResult<VariableRevisionResponse>;

export interface VariableHistoryProps {
  currentRevisionId: string;
  history: VariableHistoryResult;
}

// A historical row may be masked even when its own sensitivity flag is false.
const MASKED_PII_VALUE = "[redacted]";

const contentFields = [
  ["value_type", "Value type"],
  ["default_value", "Default value"],
  ["allowed_values", "Allowed values"],
  ["is_required", "Required"],
  ["is_sensitive", "Sensitive"],
] as const;

type ContentField = (typeof contentFields)[number][0];

type FieldChange = {
  label: string;
  status: "changed" | "indeterminate" | "unchanged";
};

function valuesMatch(
  left: VariableRevisionResponse[ContentField],
  right: VariableRevisionResponse[ContentField],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function changedVariableFields(
  newer: VariableRevisionResponse,
  older: VariableRevisionResponse,
): FieldChange[] {
  return contentFields.map(([field, label]) => ({
    label,
    status:
      isRedacted(newer[field]) || isRedacted(older[field])
        ? "indeterminate"
        : valuesMatch(newer[field], older[field])
          ? "unchanged"
          : "changed",
  }));
}

function isRedacted(value: unknown): boolean {
  return (
    value === MASKED_PII_VALUE ||
    (Array.isArray(value) && value.some((item) => item === MASKED_PII_VALUE))
  );
}

function revisionValue(
  revision: VariableRevisionResponse,
  field: ContentField,
): string {
  const value = revision[field];
  if (field === "default_value" || field === "allowed_values") {
    if (isRedacted(value)) {
      return "Sensitive — hidden";
    }
    if (value === null) {
      return field === "default_value" ? "No default" : "No allowed values";
    }
  }
  if (field === "is_required" || field === "is_sensitive") {
    return value ? "Yes" : "No";
  }
  return formatJsonValue(value);
}

export function VariableHistory({
  currentRevisionId,
  history,
}: VariableHistoryProps) {
  return (
    <Section heading="History">
      {history.isPending ? (
        <Skeleton label="Loading history" rows={3} shape="detail-field" />
      ) : history.errorMessage !== undefined ? (
        <InlineError>{history.errorMessage}</InlineError>
      ) : history.items.length === 0 ? (
        <Text variant="muted">No revisions recorded.</Text>
      ) : (
        <Stack gap={3}>
          {/* ASSUMED(rows show created_by UUID + time; a null created_by renders
              as System, matching MarkerRevisionHistory; see
              docs/open-questions.md#marker-revision-row-content) */}
          {history.items.map((revision, index) => {
            const olderRevision = history.items[index + 1];
            const fieldChanges =
              olderRevision === undefined
                ? []
                : changedVariableFields(revision, olderRevision);
            const changedFields = fieldChanges
              .filter((field) => field.status === "changed")
              .map((field) => field.label);
            const hiddenFields = fieldChanges
              .filter((field) => field.status === "indeterminate")
              .map((field) => field.label);
            return (
              <Stack gap={1} key={revision.id}>
                <Inline align="center" gap={2}>
                  <Text as="span">
                    <strong>Revision {revision.revision}</strong>
                  </Text>
                  {revision.id === currentRevisionId ? (
                    <Badge tone="success">Current</Badge>
                  ) : null}
                </Inline>
                {contentFields.map(([field, label]) => (
                  <Text key={field} variant="muted">
                    {label}: {revisionValue(revision, field)}
                  </Text>
                ))}
                {olderRevision !== undefined ? (
                  <Text variant="muted">
                    Compared with revision {olderRevision.revision}:{" "}
                    {changedFields.length === 0 && hiddenFields.length === 0
                      ? "No content fields changed"
                      : [
                          changedFields.length === 0
                            ? null
                            : `Changed ${changedFields.join(", ")}`,
                          hiddenFields.length === 0
                            ? null
                            : `${hiddenFields.join(", ")} hidden`,
                        ]
                          .filter((text): text is string => text !== null)
                          .join(" · ")}
                  </Text>
                ) : null}
                <Text variant="muted">
                  {revision.created_by === null
                    ? "System"
                    : `Created by ${revision.created_by}`}
                  {` · ${formatDateTime(revision.created_at)}`}
                </Text>
              </Stack>
            );
          })}
          {history.hasNextPage ? (
            <Inline>
              <Button
                disabled={history.isFetchingNextPage}
                size="sm"
                type="button"
                variant="secondary"
                onClick={history.fetchNextPage}
              >
                {history.isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </Inline>
          ) : null}
        </Stack>
      )}
    </Section>
  );
}
